import PageTemplate from "@/components/page-template/PageTemplate";
import withTenantAdminRequired from "@/hoc/withTenantAdminRequired";
import {
  Users,
  Settings as SettingsIcon,
  Menu,
  Shield,
  User,
  Database,
  ChevronRight,
  Key,
  CreditCard,
  Wrench,
  Mail,
  Bell,
  LogIn,
  Plug,
  BotMessageSquare,
  MessageCircle,
} from "lucide-react";
import { siApple } from "simple-icons";
import { Box, Card, Flex, Grid, Heading, Text } from "@radix-ui/themes";
import { Link } from "react-router-dom";
import { useAuth } from "@/providers/AuthProvider";

const AppleBrandIcon = ({
  className, style,
}: { className?: string; style?: React.CSSProperties }) => (
  <svg viewBox="0 0 24 24" fill={`#${siApple.hex}`} className={className} style={style}>
    <path d={siApple.path} />
  </svg>
);

// Hand-authored — simple-icons still ships the pre-2024 Stripe glyph.
const StripeBrandIcon = ({
  className, style,
}: { className?: string; style?: React.CSSProperties }) => (
  <svg viewBox="0 0 24 24" className={className} style={style}>
    <rect width="24" height="24" rx="4" fill="#635BFF" />
    <path d="M5.4 9.4 L18.6 7.2 L18.6 14.6 L5.4 16.8 Z" fill="#FFFFFF" />
  </svg>
);

function SectionCard({ section, safeNavigate }: { section: any; safeNavigate: (href: string) => void }) {
  return (
    <Card size="3">
      <Box p="1">
        <Flex align="center" gap="3" mb="4">
          <section.icon className="w-6 h-6" style={{ color: "var(--accent-9)" }} />
          <Box>
            <Heading as="h2" size="5" weight="medium">{section.title}</Heading>
            <Text size="2" color="gray" mt="1">{section.description}</Text>
          </Box>
        </Flex>
        <Flex direction="column" gap="3">
          {section.items.map((item: any) => (
            <Link
              key={item.name}
              to={item.href}
              onClick={(e) => { e.preventDefault(); safeNavigate(item.href); }}
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <Box
                p="4"
                style={{
                  border: "1px solid var(--gray-6)",
                  borderRadius: "var(--radius-3)",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                className="hover:bg-gray-2 hover:border-gray-7"
              >
                <Flex align="center" justify="between">
                  <Flex align="center" gap="3">
                    <item.icon className="w-5 h-5" style={{ color: "var(--gray-11)" }} />
                    <Box>
                      <Heading as="h3" size="3" weight="medium">{item.name}</Heading>
                      <Text size="2" color="gray" mt="1">{item.description}</Text>
                    </Box>
                  </Flex>
                  <ChevronRight className="w-5 h-5" style={{ color: "var(--gray-9)" }} />
                </Flex>
              </Box>
            </Link>
          ))}
        </Flex>
      </Box>
    </Card>
  );
}

export default withTenantAdminRequired(function SettingsPage() {
  const { currentTenant, safeNavigate } = useAuth();
  const settingSections = [
    {
      title: "Platform Settings",
      description: "Configure core platform functionality",
      icon: SettingsIcon,
      items: [
        {
          name: "Organisation Settings",
          description: "Manage your organisation preferences and profile",
          href: `/org/${currentTenant?.id}/settings/organisation`,
          icon: User,
        },
        {
          name: "Data Model",
          description: "Configure your data structure and fields",
          href: `/org/${currentTenant?.id}/settings/data-model`,
          icon: Database,
        },
        {
          name: "Menu Configuration",
          description: "Customise navigation and menu structure",
          href: `/org/${currentTenant?.id}/settings/menu`,
          icon: Menu,
        },
        {
          name: "Secrets",
          description: "Manage application secrets and environment variables",
          href: `/org/${currentTenant?.id}/settings/secrets`,
          icon: Key,
        },
        {
          name: "Search Operations",
          description: "Manage search indexing, embeddings, and maintenance",
          href: `/org/${currentTenant?.id}/settings/operations`,
          icon: Wrench,
        },
      ],
    },
    {
      title: "Communications",
      description: "Configure how this organisation reaches its users",
      icon: MessageCircle,
      items: [
        {
          name: "Email Templates",
          description: "Manage and customize email templates",
          href: `/org/${currentTenant?.id}/settings/email-templates`,
          icon: Mail,
        },
        {
          name: "Push Notifications",
          description: "Connect FCM, generate VAPID keys, and view registered devices",
          href: `/org/${currentTenant?.id}/settings/push-notifications`,
          icon: Bell,
        },
      ],
    },
    {
      title: "Payments",
      description: "Configure how this organisation takes payments",
      icon: CreditCard,
      items: [
        {
          name: "Stripe",
          description: "Connect a Stripe account for web and mobile payments, and manage processing fees",
          href: `/org/${currentTenant?.id}/settings/payments?tab=stripe`,
          icon: StripeBrandIcon,
        },
        {
          name: "Apple In-App Purchase",
          description: "Configure App Store Connect for iOS purchases",
          href: `/org/${currentTenant?.id}/settings/payments?tab=apple`,
          icon: AppleBrandIcon,
        },
      ],
    },
    {
      title: "Users & Roles",
      description: "Control user access and permissions",
      icon: Users,
      items: [
        {
          name: "Users",
          description: "Manage user accounts and access",
          href: `/org/${currentTenant?.id}/settings/users`,
          icon: Users,
        },
        {
          name: "Roles and Permissions",
          description: "Configure user roles and access controls",
          href: `/org/${currentTenant?.id}/settings/roles-and-permissions`,
          icon: Shield,
        },
        {
          name: "Groups",
          description: "Bundle users into teams and share row-level access",
          href: `/org/${currentTenant?.id}/settings/groups`,
          icon: Users,
        },
      ],
    },
    {
      title: "Integrations",
      description: "Connect external services and authentication",
      icon: Plug,
      items: [
        {
          name: "Social Sign-In",
          description: "Configure social authentication providers",
          href: `/org/${currentTenant?.id}/settings/integrations?tab=social`,
          icon: LogIn,
        },
        {
          name: "Integrations",
          description: "Connect external services like Slack and Google Calendar",
          href: `/org/${currentTenant?.id}/settings/integrations?tab=integrations`,
          icon: Plug,
        },
      ],
    },
    {
      title: "AI & Automation",
      description: "Configure AI models and automation tools",
      icon: BotMessageSquare,
      items: [
        {
          name: "AI Settings",
          description: "Configure AI models and bring your own API keys",
          href: `/org/${currentTenant?.id}/settings/ai`,
          icon: BotMessageSquare,
        },
      ],
    },
  ];

  return (
    <PageTemplate
      title="Settings"
      description="Configure your platform settings and user management"
    >
      <Grid columns="2" gap="6">
        <Flex direction="column" gap="6">
          {settingSections.slice(0, 3).map((section) => (
            <SectionCard key={section.title} section={section} safeNavigate={safeNavigate} />
          ))}
        </Flex>

        <Flex direction="column" gap="6">
          {settingSections.slice(3).map((section) => (
            <SectionCard key={section.title} section={section} safeNavigate={safeNavigate} />
          ))}
        </Flex>
      </Grid>
    </PageTemplate>
  );
});
